import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { ReactNode, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Check, ChevronDown } from "lucide-react";
import { Box } from "@ui/box/Box";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { fitTabs } from "./tabOverflow";

export interface AdaptiveTab {
	id: string;
	label: string;
	content?: ReactNode;
	selected: boolean;
	onSelect: () => void;
}

const Root = styled.div`
	display: flex; align-items: center; min-width: 0; max-width: 100%; flex: 0 0 auto; position: relative;
	.tab-viewport { flex: 1 1 auto; min-width: 0; }
	.tab-row { display: flex; align-items: stretch; min-width: 0; }
	.tab-button { display: flex; align-items: stretch; flex: 0 0 auto; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; cursor: pointer; min-width: 0; }
	.tab-row > .tab-button[aria-selected="true"] { flex-shrink: 1; }
	.tab-row > .tab-button > * { min-width: 0; max-width: 100%; flex-shrink: 1; box-sizing: border-box; }
	.tab-row .tab-label { overflow: hidden; text-overflow: ellipsis; }
	.tab-label { padding: 11px 16px; color: #667085; font: 500 13px/1.5 "Euclid Circular B", sans-serif; white-space: nowrap; border-bottom: 2px solid transparent; }
	.tab-button[aria-selected="true"] .tab-label { border-color: #344054; color: #182230; font-weight: 600; }
	.tab-more { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; gap: 6px; margin-left: auto; padding: 11px 16px; border: 0; border-bottom: 2px solid transparent; background: transparent; color: #667085; font: 500 13px/1.5 "Euclid Circular B", sans-serif; white-space: nowrap; cursor: pointer; }
	.tab-more.custom { align-items: stretch; padding: 0; border: 0; color: inherit; font: inherit; }
	.tab-more[data-active="true"] { box-shadow: inset 0 -2px #344054; }
	.tab-more:hover, .tab-button:hover { background: #f5f7fa; }
	button:focus-visible { outline: 2px solid #528bff; outline-offset: -2px; border-radius: 5px; }
	.tab-measure { display: flex; position: absolute; width: max-content; height: 0; overflow: hidden; visibility: hidden; pointer-events: none; }
	/* Reserve the emphasized label width even when another tab is selected. */
	.tab-measure .tab-button *, .tab-measure .tab-more * { font-weight: var(--tab-measure-weight, 600) !important; }
	.tab-accessory { display: flex; align-items: center; flex: 0 0 auto; }
`;

const Menu = styled.div`
	position: fixed; z-index: 2147483647; box-sizing: border-box; overflow-y: auto; overscroll-behavior: contain;
	padding: 6px; border: 1px solid #d0d5dd; border-radius: 10px; background: #fff;
	box-shadow: 0 8px 24px rgba(16,24,40,.14); font: 13px/1.5 "Euclid Circular B", sans-serif;
	button { display: flex; align-items: center; justify-content: space-between; gap: 12px; box-sizing: border-box; width: 100%; border: 0; border-radius: 6px; padding: 10px 12px; background: transparent; color: #344054; font: inherit; text-align: left; overflow-wrap: anywhere; cursor: pointer; }
	button:hover, button:focus { background: #f5f7fa; }
	button:focus-visible { outline: 2px solid #528bff; outline-offset: -2px; }
`;

/** Shared by page tabs, popup tabs and compact tab selectors. Menu escapes clipping ancestors. */
export function AdaptiveTabBar({ items, sx, before, after, gap = 0, label = "Sections", className, renderMore }: {
	items: AdaptiveTab[]; sx?: RecursiveCSS; before?: ReactNode; after?: ReactNode; gap?: number; label?: string; className?: string; renderMore?: (count: number) => ReactNode;
}) {
	useLocale();
	const root = useRef<HTMLDivElement>(null);
	const viewport = useRef<HTMLDivElement>(null);
	const measure = useRef<HTMLDivElement>(null);
	const more = useRef<HTMLButtonElement>(null);
	const menu = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState<number[]>([]);
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState({ left: 0, top: 0, width: 230, maxHeight: 360 });
	const id = useId();
	const active = items.findIndex(item => item.selected);
	const signature = JSON.stringify(items.map(item => [item.id, item.label]));

	useLayoutEffect(() => {
		const update = () => {
			if (!viewport.current || !measure.current) return;
			const widths = Array.from(measure.current.children).map(node => node.getBoundingClientRect().width);
			const moreWidth = widths.pop() ?? 100;
			const next = fitTabs(widths, viewport.current.clientWidth, moreWidth, gap, active);
			setVisible(previous => previous.join() === next.join() ? previous : next);
		};
		update();
		const observer = new ResizeObserver(update);
		if (viewport.current) observer.observe(viewport.current);
		if (measure.current) Array.from(measure.current.children).forEach(node => observer.observe(node));
		return () => observer.disconnect();
	}, [signature, gap, active]);

	const hidden = items.map((_, index) => index).filter(index => !visible.includes(index));
	const activeHidden = hidden.includes(active);
	useLayoutEffect(() => {
		if (!hidden.length) setOpen(false);
	}, [hidden.length]);
	useLayoutEffect(() => {
		if (!open) return;
		const place = () => {
			const anchor = more.current?.getBoundingClientRect();
			if (!anchor) return;
			const width = Math.min(260, window.innerWidth - 16);
			const below = window.innerHeight - anchor.bottom - 14;
			const above = anchor.top - 14;
			const maxHeight = Math.max(40, Math.min(360, Math.max(below, above)));
			const height = Math.min(menu.current?.scrollHeight ?? 360, maxHeight);
			setPosition({
				left: Math.max(8, Math.min(anchor.right - width, window.innerWidth - width - 8)),
				top: below >= height ? anchor.bottom + 6 : Math.max(8, anchor.top - height - 6),
				width, maxHeight,
			});
		};
		place();
		menu.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
		const outside = (event: PointerEvent) => {
			if (!root.current?.contains(event.target as Node) && !menu.current?.contains(event.target as Node)) setOpen(false);
		};
		// Capture before popup-level Escape handlers and focus traps.
		const keys = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault(); event.stopImmediatePropagation(); setOpen(false); more.current?.focus({ preventScroll: true });
			}
			if (event.key === "Tab" && menu.current?.contains(document.activeElement)) {
				event.preventDefault(); event.stopImmediatePropagation(); setOpen(false); more.current?.focus({ preventScroll: true });
			}
		};
		document.addEventListener("pointerdown", outside);
		document.addEventListener("keydown", keys, true);
		window.addEventListener("resize", place);
		window.addEventListener("scroll", place, true);
		return () => {
			document.removeEventListener("pointerdown", outside);
			document.removeEventListener("keydown", keys, true);
			window.removeEventListener("resize", place);
			window.removeEventListener("scroll", place, true);
		};
	}, [open]);

	const select = (index: number) => {
		items[index].onSelect();
		setOpen(false);
		requestAnimationFrame(() => {
			const target = document.getElementById(`${id}-tab-${index}`);
			(target ?? more.current)?.focus({ preventScroll: true });
		});
	};
	const content = (index: number) => items[index].content ?? <span className="tab-label">{items[index].label}</span>;
	const moreContent = (count: number) => renderMore ? renderMore(count) : <>{msg("more")}{count}) <ChevronDown size={14} /></>;
	const moreClass = `tab-more${renderMore ? " custom" : ""}`;
	return <Box sx={{ minWidth: 0, maxWidth: "100%", flexShrink: 0, ...sx }}>
		<Root ref={root} className={className} onBlur={event => {
			if (!root.current?.contains(event.relatedTarget as Node) && !menu.current?.contains(event.relatedTarget as Node)) setOpen(false);
		}}>
			{before && <div className="tab-accessory">{before}</div>}
			<div className="tab-viewport" ref={viewport}>
				<div className="tab-measure" ref={measure} aria-hidden="true">
					{items.map((item, index) => <span className="tab-button" key={item.id}>{content(index)}</span>)}
					<span className={moreClass}>{moreContent(items.length)}</span>
				</div>
				<div className="tab-row" role="tablist" aria-label={label} style={{ gap }}>
					{visible.map(index => <button className="tab-button" type="button" role="tab" id={`${id}-tab-${index}`}
						key={items[index].id} title={items[index].label} aria-selected={items[index].selected} tabIndex={index === active || (activeHidden && index === visible[0]) ? 0 : -1}
						onClick={() => select(index)} onKeyDown={event => {
							const current = visible.indexOf(index);
							const target = event.key === "ArrowRight" ? visible[(current + 1) % visible.length]
								: event.key === "ArrowLeft" ? visible[(current - 1 + visible.length) % visible.length]
								: event.key === "Home" ? visible[0] : event.key === "End" ? visible[visible.length - 1] : undefined;
							if (target !== undefined) { event.preventDefault(); select(target); }
						}}>{content(index)}</button>)}
					{hidden.length > 0 && <button className={moreClass} ref={more} type="button" aria-haspopup="menu"
						data-active={activeHidden} title={activeHidden ? msg("templateSelected_1a03b743", { v0: items[active].label }) : undefined}
						aria-expanded={open} aria-controls={open ? `${id}-menu` : undefined} onClick={() => setOpen(value => !value)}
						onKeyDown={event => { if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); } }}>
						{moreContent(hidden.length)}
					</button>}
				</div>
			</div>
			{after && <div className="tab-accessory">{after}</div>}
			{open && hidden.length > 0 && createPortal(<Menu ref={menu} id={`${id}-menu`} role="menu" aria-label={msg("moreSections")}
				style={position} onKeyDown={event => {
					const buttons = Array.from(menu.current?.querySelectorAll("button") ?? []);
					const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
					const next = event.key === "ArrowDown" ? (current + 1) % buttons.length
						: event.key === "ArrowUp" ? (current - 1 + buttons.length) % buttons.length
						: event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : -1;
					if (next >= 0) { event.preventDefault(); buttons[next]?.focus(); }
				}}>
				{hidden.map(index => <button type="button" role="menuitemradio" aria-checked={items[index].selected} key={items[index].id} onClick={() => select(index)}>
					{items[index].label}{items[index].selected && <Check size={14} />}
				</button>)}
			</Menu>, document.body)}
		</Root>
	</Box>;
}
